import React, { useEffect, useMemo, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { useTranslation } from "../i18n/I18nContext";
import { listFrequencies, listSubjectsByLevel } from "../services/teacher";
import { listClassrooms } from "../services/classroom";
import { listGradebooks, finalizeGradebook, publishGradebook, requestChange, authorizeChange } from "../services/gradebook";
import { isProfessor, isDirectorPedagogico, canEditGradebookAnyStatus } from "../services/auth";
import { GRADEBOOK_STATUS_BADGE, GRADEBOOK_STATUS_LABEL_KEY } from "../utils/gradebookStatus";
import { levelKey } from "../utils/classesLabels";

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: currentYear + 1 - 2024 + 1 }, (_, i) => 2024 + i);
const STATUS_OPTIONS = Object.keys(GRADEBOOK_STATUS_LABEL_KEY);
// Matches CadastrarAvaliacaoLayer/ListaAvaliacoesLayer's PERIOD_OPTIONS — the
// gradebook's `period` is chosen by whoever creates it (LancamentoNotasLayer),
// independent of the evaluation's own `period` — the same evaluation can have
// a separate pauta per trimestre.
// Values match sge_infantil_web's `shifts` (src/pages/grade/list/index.js)
// and every gradebook/evaluation record migrated from it — real data uses
// these full labels as the `period` value, not "1"/"2"/"3".
const PERIOD_OPTIONS = [
  { value: "Primeiro Trimestre", label: "1º Trimestre" },
  { value: "Segundo Trimestre", label: "2º Trimestre" },
  { value: "Terceiro Trimestre", label: "3º Trimestre" },
];

const formatDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (isNaN(date.getTime())) return null;
  return date.toLocaleDateString("pt-PT", { day: "2-digit", month: "short", year: "numeric" });
};

// Fields the row's own columns don't show (teacher excluded on purpose,
// see the mobile card below) — surfaced in the desktop table's expanded
// panel above the student grades, and directly on every mobile card.
const buildGradebookDetails = (gb, t) =>
  [
    { label: t(levelKey("turma.field.educationLevel")), value: gb.level?.description },
    { label: t("class.evaluationDate"), value: formatDate(gb.evaluationDate) },
    { label: t("nota.detail.degree"), value: gb.degree },
    { label: t(levelKey("nota.detail.degreeLevel")), value: gb.degreeLevel },
    { label: t("nota.detail.teacherNotes"), value: gb.teacherNotes },
    { label: t("nota.detail.notes"), value: gb.notes },
  ].filter((item) => item.value);

const ListaNotasLayer = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [year, setYear] = useState(currentYear);
  const [frequencies, setFrequencies] = useState([]);
  const [levelId, setLevelId] = useState("");
  const [classrooms, setClassrooms] = useState([]);
  const [classId, setClassId] = useState("");
  const [subjects, setSubjects] = useState([]);
  const [subjectId, setSubjectId] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [periodFilter, setPeriodFilter] = useState("");

  const [gradebooks, setGradebooks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    listFrequencies().then((resp) => setFrequencies(resp.data || [])).catch(() => setFrequencies([]));
  }, []);

  useEffect(() => {
    listClassrooms(year).then((resp) => setClassrooms(resp.data || [])).catch(() => setClassrooms([]));
  }, [year]);

  const classOptions = useMemo(() => classrooms.filter((c) => !levelId || c.educationLevel === levelId), [classrooms, levelId]);

  useEffect(() => {
    setSubjectId("");
    if (!levelId) {
      setSubjects([]);
      return;
    }
    listSubjectsByLevel(levelId).then((resp) => setSubjects(resp.data || [])).catch(() => setSubjects([]));
  }, [levelId]);

  // Disciplina/Estado/Trimestre filtram a lista já carregada — não há
  // necessidade de ir ao servidor outra vez para isto (mesmo padrão do
  // filtro de Disciplina em Pautas).
  const filteredGradebooks = useMemo(
    () =>
      gradebooks.filter(
        (gb) =>
          (!subjectId || gb.subjectId === subjectId) &&
          (!statusFilter || gb.status === statusFilter) &&
          (!periodFilter || String(gb.period) === periodFilter),
      ),
    [gradebooks, subjectId, statusFilter, periodFilter],
  );

  const fetchGradebooks = () => {
    setLoading(true);
    // Gradebook records don't store a `year` field (only evaluationDate) —
    // passing year here would filter out every real record. `year` only
    // narrows which turmas appear in the selector above.
    listGradebooks({ levelId: levelId || undefined, classId: classId || undefined })
      .then((resp) => setGradebooks(resp.data || []))
      .catch(() => toast.error(t("nota.error.load")))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchGradebooks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [levelId, classId]);

  const periodLabel = (gb) => PERIOD_OPTIONS.find((p) => p.value === String(gb.period))?.label || "-";

  const gradedStats = (gb) => {
    const grades = gb.grades || [];
    const graded = grades.filter((g) => g.grade !== null && g.grade !== undefined && g.grade !== "").length;
    const percent = grades.length ? Math.round((graded / grades.length) * 100) : 0;
    return { graded, total: grades.length, percent };
  };

  const clearFilters = () => {
    setLevelId("");
    setClassId("");
    setSubjectId("");
    setStatusFilter("");
    setPeriodFilter("");
  };

  const toggleExpand = (id) => setExpandedId((cur) => (cur === id ? null : id));

  // Deep-links Lançamento de Notas straight into this gradebook's grades,
  // skipping the manual level/turma/disciplina/avaliação re-selection.
  // Passes the gradebook we already have in hand instead of just its ids —
  // re-deriving it there via the level→turma→disciplina→avaliação cascade
  // was fragile (any mismatch anywhere in that chain silently left the user
  // stuck on step 1 with no gradebook and no error).
  const handleEdit = (gb) => {
    navigate("/academico/lancamento-notas", { state: { editGradebook: gb } });
  };

  // Opens PautaVisualizarLayer — a single focused card (back button + this
  // pauta's details + student grades) with none of this screen's filters or
  // other cards around it. Ships the period label/graded stats we already
  // computed here instead of making that screen re-derive them.
  const handleView = (gb) => {
    navigate("/academico/lista-notas/visualizar", {
      state: { gradebook: gb, periodLabel: periodLabel(gb), gradedStats: gradedStats(gb) },
    });
  };

  const runAction = async (gb, actionFn, successKey, errorKey) => {
    setBusyId(gb.id);
    try {
      await actionFn(gb.id);
      toast.success(t(successKey));
      fetchGradebooks();
    } catch (err) {
      toast.error(err?.response?.data?.error || t(errorKey));
    } finally {
      setBusyId(null);
    }
  };

  // Shared between the desktop table row and the mobile card so the two
  // layouts (see the render below) never drift out of sync on which action
  // is available for which status/role.
  const renderActions = (gb) => (
    <div className='d-flex flex-wrap gap-2'>
      <button
        type='button'
        className='btn btn-outline-primary-600 btn-sm d-inline-flex align-items-center gap-1'
        onClick={() => handleView(gb)}
      >
        <Icon icon='mdi:eye-outline' />
        {t("nota.action.view")}
      </button>
      {/* Admin, Director Pedagógico e Super Admin podem editar a pauta seja
          qual for o estado actual dela. */}
      {canEditGradebookAnyStatus() && (
        <button type='button' className='btn btn-outline-secondary btn-sm' onClick={() => handleEdit(gb)}>
          {t("nota.action.edit")}
        </button>
      )}
      {gb.status === "DRAFT" && (
        <button
          type='button'
          className='btn btn-outline-success-600 btn-sm'
          disabled={busyId === gb.id}
          onClick={() => runAction(gb, finalizeGradebook, "nota.success.finalized", "nota.error.finalize")}
        >
          {t("nota.action.finalize")}
        </button>
      )}
      {/* Também disponível em CHANGE: uma pauta com alteração solicitada
          pode ser publicada directamente, sem ter de passar primeiro por
          "Aceitar Pedido". */}
      {(gb.status === "FINALIZED" || gb.status === "CHANGE") && (
        <button
          type='button'
          className='btn btn-outline-primary-600 btn-sm'
          disabled={busyId === gb.id}
          onClick={() => runAction(gb, publishGradebook, "nota.success.published", "nota.error.publish")}
        >
          {t("nota.action.publish")}
        </button>
      )}
      {/* Só o Professor pode pedir a reabertura de uma pauta já finalizada/publicada. */}
      {(gb.status === "FINALIZED" || gb.status === "PUBLISHED") && isProfessor() && (
        <button
          type='button'
          className='btn btn-outline-danger-600 btn-sm'
          disabled={busyId === gb.id}
          onClick={() => runAction(gb, requestChange, "nota.success.changeRequested", "nota.error.change")}
        >
          {t("nota.action.requestChange")}
        </button>
      )}
      {/* Só o Director Pedagógico pode aceitar o pedido de alteração. */}
      {gb.status === "CHANGE" && isDirectorPedagogico() && (
        <button
          type='button'
          className='btn btn-outline-warning-600 btn-sm'
          disabled={busyId === gb.id}
          onClick={() => runAction(gb, authorizeChange, "nota.success.changeAuthorized", "nota.error.authorizeChange")}
        >
          {t("nota.action.acceptChangeRequest")}
        </button>
      )}
    </div>
  );

  // Also shared between the two layouts — the expanded panel's content
  // (gradebook details + per-student grades) doesn't change with the
  // breakpoint, only the row/card it hangs off of.
  const renderExpandedDetails = (gb) => {
    const studentGrades = (gb.grades || [])
      .slice()
      .sort((a, b) => (a.student?.name || "").localeCompare(b.student?.name || "", "pt"));
    const details = buildGradebookDetails(gb, t);
    return (
      <>
        {details.length > 0 && (
          <div className='table-responsive mb-16'>
            <table className='table bordered-table table-sm mb-0 bg-base'>
              <thead>
                <tr>
                  {details.map((item) => (
                    <th key={item.label}>{item.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  {details.map((item) => (
                    <td key={item.label}>{item.value}</td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        )}
        {studentGrades.length === 0 ? (
          <div className='alert alert-info mb-0'>{t("nota.hint.noStudentsGraded")}</div>
        ) : (
          <div className='table-responsive'>
            <table className='table bordered-table mb-0'>
              <thead>
                <tr>
                  <th>{t("presenca.col.name")}</th>
                  <th>{t("nota.col.grade")}</th>
                </tr>
              </thead>
              <tbody>
                {studentGrades.map((g, idx) => {
                  const hasGrade = g.grade !== null && g.grade !== undefined && g.grade !== "";
                  const passed = hasGrade && Number(g.grade) >= 10;
                  return (
                    <tr key={g.student?.id || idx}>
                      <td className='fw-medium'>{g.student?.name || "-"}</td>
                      <td>
                        <span className={hasGrade ? (passed ? "text-success-main fw-semibold" : "text-danger-main fw-semibold") : "text-secondary-light"}>
                          {hasGrade ? g.grade : "-"}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </>
    );
  };

  return (
    <div className='row gy-4'>
      <ToastContainer />

      <div className='col-12'>
        <div className='card radius-12'>
          <div className='card-body'>
            <div className='d-flex align-items-center gap-2 mb-16'>
              <span className='w-32-px h-32-px bg-primary-50 text-primary-600 rounded-circle d-flex justify-content-center align-items-center text-lg'>
                <Icon icon='mdi:filter-variant' />
              </span>
              <h6 className='text-md mb-0'>{t("global.filters")}</h6>
            </div>
            <div className='p-16 radius-8 bg-neutral-50'>
              <div className='row gy-3 align-items-end'>
                <div className='col-sm-6 col-lg-2'>
                  <label className='form-label text-sm mb-8'>{t("turma.field.academicYear")}</label>
                  <select className='form-control form-select' value={year} onChange={(e) => setYear(Number(e.target.value))}>
                    {YEAR_OPTIONS.map((y) => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                  </select>
                </div>
                <div className='col-sm-6 col-lg-2'>
                  <label className='form-label text-sm mb-8'>{t(levelKey("turma.field.educationLevel"))}</label>
                  <select className='form-control form-select' value={levelId} onChange={(e) => { setLevelId(e.target.value); setClassId(""); }}>
                    <option value=''>{t(levelKey("disciplina.filter.allLevels"))}</option>
                    {frequencies.map((f) => (
                      <option key={f.id} value={f.id}>
                        {f.description}
                      </option>
                    ))}
                  </select>
                </div>
                <div className='col-sm-6 col-lg-2'>
                  <label className='form-label text-sm mb-8'>{t("turma.col.className")}</label>
                  <select className='form-control form-select' value={classId} onChange={(e) => setClassId(e.target.value)}>
                    <option value=''>{t("nota.filter.allClasses")}</option>
                    {classOptions.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.className}
                      </option>
                    ))}
                  </select>
                </div>
                <div className='col-sm-6 col-lg-2'>
                  <label className='form-label text-sm mb-8'>{t("disciplina.field.subjectName")}</label>
                  <select className='form-control form-select' value={subjectId} onChange={(e) => setSubjectId(e.target.value)} disabled={!levelId}>
                    <option value=''>{t("pauta.filter.allSubjects")}</option>
                    {subjects.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.subjectName}
                      </option>
                    ))}
                  </select>
                </div>
                <div className='col-sm-6 col-lg-2'>
                  <label className='form-label text-sm mb-8'>{t("nota.filter.status")}</label>
                  <select className='form-control form-select' value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                    <option value=''>{t("nota.filter.allStatus")}</option>
                    {STATUS_OPTIONS.map((s) => (
                      <option key={s} value={s}>
                        {t(GRADEBOOK_STATUS_LABEL_KEY[s])}
                      </option>
                    ))}
                  </select>
                </div>
                <div className='col-sm-6 col-lg-1'>
                  <label className='form-label text-sm mb-8'>{t("nota.filter.period")}</label>
                  <select className='form-control form-select' value={periodFilter} onChange={(e) => setPeriodFilter(e.target.value)}>
                    <option value=''>{t("nota.filter.allPeriods")}</option>
                    {PERIOD_OPTIONS.map((p) => (
                      <option key={p.value} value={p.value}>
                        {p.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className='col-sm-6 col-lg-1'>
                  <button
                    type='button'
                    className='btn btn-outline-secondary w-100 d-flex align-items-center justify-content-center'
                    title={t("global.clean.fields")}
                    onClick={clearFilters}
                  >
                    <Icon icon='ic:round-refresh' />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className='col-12'>
        <div className='card radius-12'>
          <div className='card-body'>
            {loading ? (
              <div className='text-center py-40'>{t("global.loading")}</div>
            ) : filteredGradebooks.length === 0 ? (
              <div className='alert alert-info mb-0'>{t("nota.hint.noGradebooks")}</div>
            ) : (
              <>
                {/* Tablet/desktop: full table. Professor is intentionally
                    left out here too (see PautaVisualizarLayer — it's still
                    one click away via "Visualizar" for whoever needs it).
                    Below lg it gives way to the horizontal card list — a
                    table this dense turns into a scroll-to-read-anything
                    mess on a phone. */}
                <div className='d-none d-lg-block table-responsive'>
                  <table className='table bordered-table mb-0'>
                    <thead>
                      <tr>
                        <th style={{ width: 40 }} />
                        <th>{t("turma.col.className")}</th>
                        <th>{t("disciplina.field.subjectName")}</th>
                        <th>{t("nota.field.evaluation")}</th>
                        <th>{t("nota.filter.period")}</th>
                        <th>{t("global.status")}</th>
                        <th>{t("nota.col.grade")}</th>
                        <th>{t("turma.col.actions")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredGradebooks.map((gb) => {
                        const isExpanded = expandedId === gb.id;
                        const { graded, total } = gradedStats(gb);
                        return (
                          <React.Fragment key={gb.id}>
                            <tr role='button' onClick={() => toggleExpand(gb.id)} className={isExpanded ? "bg-neutral-50" : ""}>
                              <td>
                                <Icon
                                  icon={isExpanded ? "ic:round-expand-less" : "ic:round-expand-more"}
                                  className='text-secondary-light text-xl'
                                />
                              </td>
                              <td>{gb.classroom?.name}</td>
                              <td>{gb.subject?.name}</td>
                              <td>{gb.evaluation?.name}</td>
                              <td>{periodLabel(gb)}</td>
                              <td>
                                <span className={`badge ${GRADEBOOK_STATUS_BADGE[gb.status] || "bg-neutral-200 text-neutral-600"}`}>
                                  {t(GRADEBOOK_STATUS_LABEL_KEY[gb.status]) || gb.status}
                                </span>
                              </td>
                              <td>{`${graded}/${total}`}</td>
                              <td onClick={(e) => e.stopPropagation()}>{renderActions(gb)}</td>
                            </tr>
                            {isExpanded && (
                              <tr>
                                <td colSpan={8} className='p-0'>
                                  <div className='p-24 bg-neutral-25 border-top'>{renderExpandedDetails(gb)}</div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Mobile: horizontal cards, one per pauta. Every detail the
                    pauta has (except the professor — kept off this compact
                    listing on purpose) shows directly on the card; the
                    per-student grades move to the dedicated "Visualizar"
                    screen below instead of an in-card accordion, so the
                    card itself stays short no matter the turma's size. */}
                <div className='d-lg-none d-flex flex-column gap-3'>
                  {filteredGradebooks.map((gb) => {
                    const { graded, total, percent } = gradedStats(gb);
                    const details = buildGradebookDetails(gb, t);
                    return (
                      <div key={gb.id} className='border radius-12 overflow-hidden'>
                        <div className='d-flex align-items-center gap-3 p-16 bg-neutral-25'>
                          <span className='w-40-px h-40-px flex-shrink-0 bg-primary-50 text-primary-600 rounded-circle d-flex align-items-center justify-content-center text-lg'>
                            <Icon icon='mdi:book-open-variant' />
                          </span>
                          <div className='flex-grow-1' style={{ minWidth: 0 }}>
                            <div className='d-flex align-items-center justify-content-between gap-2'>
                              <h6 className='mb-0 text-sm text-truncate'>{gb.subject?.name}</h6>
                              <span className={`badge flex-shrink-0 ${GRADEBOOK_STATUS_BADGE[gb.status] || "bg-neutral-200 text-neutral-600"}`}>
                                {t(GRADEBOOK_STATUS_LABEL_KEY[gb.status]) || gb.status}
                              </span>
                            </div>
                            <span className='text-secondary-light text-xs text-truncate d-block'>
                              {gb.classroom?.name} · {gb.evaluation?.name} · {periodLabel(gb)}
                            </span>
                          </div>
                        </div>

                        <div className='p-16 border-top'>
                          {details.length > 0 && (
                            <div className='row row-cols-2 g-2 mb-12'>
                              {details.map((item) => (
                                <div key={item.label}>
                                  <span className='text-secondary-light text-xs d-block'>{item.label}</span>
                                  <span className='text-sm fw-medium text-truncate d-block'>{item.value}</span>
                                </div>
                              ))}
                            </div>
                          )}

                          <div className='d-flex align-items-center gap-8 mb-12'>
                            <div
                              className='progress bg-primary-50 rounded-pill h-8-px'
                              style={{ width: 96 }}
                              role='progressbar'
                              aria-valuenow={graded}
                              aria-valuemin={0}
                              aria-valuemax={total}
                            >
                              <div className='progress-bar bg-primary-600 rounded-pill' style={{ width: `${percent}%` }} />
                            </div>
                            <span className='text-secondary-light text-xs text-nowrap'>{`${graded}/${total}`}</span>
                          </div>

                          {renderActions(gb)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListaNotasLayer;
